import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { observer } from 'mobx-react';

import type { PlayUserType } from '@genially/p2p-lib/build/module';
import { calculate } from '@genially/utils';

type PlaySessionUserInfo =
  | {
      type: PlayUserType.Host;
      geniallyId: string;
      teamId: string;
      sessionId: string;
    }
  | {
      type: PlayUserType.Player;
      username: string;
      geniallyId: string;
      sessionId: string;
    };

type PlaySessionInfoActions = {
  clearSession: () => void;
  setUser: (user: PlaySessionUserInfo) => void;
};

export type PlaySessionInfoContextType = PlaySessionInfoActions &
  (
    | {
        isActive: false;
      }
    | {
        isActive: true;
        user: PlaySessionUserInfo;
      }
  );

const PlaySessionInfoProviderContext = createContext({});

export interface PlaySessionInfoProps {
  children: ReactNode;
}

const PlaySessionInfoProvider = observer(({ children }: PlaySessionInfoProps) => {
  const [user, setUser] = useState<PlaySessionUserInfo | undefined>(undefined);

  const clearSession = useCallback(() => {
    setUser(undefined);
  }, []);

  const actions = useMemo<PlaySessionInfoActions>(() => {
    return {
      clearSession,
      setUser,
    };
  }, [clearSession]);

  const context = useMemo<PlaySessionInfoContextType>(() => {
    if (!user) {
      return {
        isActive: false,
        ...actions,
      };
    }

    return {
      isActive: true,
      user,
      ...actions,
    };
  }, [actions, user]);

  return (
    <PlaySessionInfoProviderContext.Provider value={context}>
      {children}
    </PlaySessionInfoProviderContext.Provider>
  );
});

const usePlaySessionInfo = () => {
  const context = useContext(
    PlaySessionInfoProviderContext,
  ) as PlaySessionInfoContextType;

  if (Object.keys(context).length === 0) {
    throw new Error('usePlaySessionInfo must be used within a PlaySessionInfoProvider');
  }

  return context;
};

export { PlaySessionInfoProvider, usePlaySessionInfo };
